import { axiosSubscriptionInstance } from "../configs/AxiosInstance";

// ✅ Fetch all employees
export const fetchEmployees = async (get) => {
  try {
    const response = await get("/fetch-employees", {
      instance: axiosSubscriptionInstance,
    });

    const employees = response?.data?.employees || [];
    const total = response?.data?.total || employees.length;

    return { total, employees };
  } catch (err) {
    console.error("Error fetching employees:", err);
    throw new Error(err?.response?.data?.message || "Error fetching employees");
  }
};

/**
 * ✅ Fetch the caller's own reporting tree — every direct and indirect report
 * beneath them, two levels deep.
 *
 * Deliberately *not* a variant of `fetchEmployees`. That endpoint walks one hop
 * upward, returns the whole tenant to anyone, and carries no depth; this one is
 * caller-scoped, walks downward, and has already applied every liveness rule
 * (`holds_role_now` on both ends of each edge, manager-assignment windows,
 * currently-active roles only) that the frontend used to reproduce by hand.
 *
 * Takes no parameters at all — the caller is the subject, resolved from the
 * token, so there is no way to ask about anyone else's org chart.
 *
 * Throws on failure, matching `fetchEmployees` above rather than
 * `CorporateWellDhanService`'s `{ success }` convention: `useCoachRoster`'s
 * catch is what drives the roster's RetryBanner.
 *
 * @param {Function} get - The `get` helper from ApiContext.
 * @returns {Promise<{ total: number, reports: Array<Object> }>}
 */
export const fetchEmployeeHierarchy = async (get) => {
  try {
    const response = await get("/fetch-employee-hierarchy", {
      instance: axiosSubscriptionInstance,
    });

    const reports = response?.data?.reports || [];
    const total = response?.data?.total ?? reports.length;

    return { total, reports };
  } catch (err) {
    console.error("Error fetching employee hierarchy:", err);
    // `status` is preserved so callers can tell a 403 ("you have no employee
    // record here") from a real failure — the former is an empty roster, not an
    // error banner.
    const error = new Error(
      err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Error fetching employee hierarchy"
    );
    error.status = err?.response?.status ?? null;
    throw error;
  }
};

// ✅ Fetch common utils (departments, roles, and potentially users)
export const fetchCommonUtils = async (get) => {
  try {
    const response = await get("/get-common-utils", {
      instance: axiosSubscriptionInstance,
    });
    return response;
  } catch (err) {
    const message =
      err?.response?.data?.message || "Failed to fetch common utils";
    console.error("Common utils fetch error:", message);
    return null;
  }
};

// ✅ Fetch all departments with roles from common utils
export const fetchDepartments = async (get) => {
  try {
    const response = await get("/get-common-utils", {
      instance: axiosSubscriptionInstance,
    });
    const departments = response?.departments_with_roles || [];
    return (
      departments.map((dept) => ({
        id: dept.id || dept.department_id || dept.title,
        name: dept.title,
        roles: dept.roles || [],
      })) || []
    );
  } catch (err) {
    const message =
      err?.response?.data?.message || "Failed to fetch departments";
    console.error("Departments fetch error:", message);
    return [];
  }
};

// バ. Modify employee roles
export const modifyEmployeeRoles = async (post, payload) => {
  try {
    const response = await post("/modify-employee-roles", payload, {
      instance: axiosSubscriptionInstance,
    });
    return { success: true, data: response?.data || response };
  } catch (err) {
    const message =
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.message ||
      "Failed to modify employee roles";
    console.error("Modify roles error:", message, err);
    throw new Error(message);
  }
};

// ✅ Delete an employee
export const deleteEmployee = async (remove, employeeId) => {
  if (!employeeId || String(employeeId) === "null" || String(employeeId) === "undefined") {
    throw new Error("Employee id is missing. Cannot delete this person.");
  }

  try {
    await remove(`/employee/${employeeId}`, {
      instance: axiosSubscriptionInstance,
    });
    return { success: true, message: "Employee deleted successfully" };
  } catch (err) {
    const message =
      err?.response?.data?.detail?.[0]?.msg ||
      err?.response?.data?.message ||
      err?.message ||
      "Failed to delete employee";
    console.error(message);
    throw new Error(typeof message === "string" ? message : "Failed to delete employee");
  }
};

// ✅ Verify employee - ADD operation
export const verifyEmployee = async (post, payload) => {
  try {
    const apiPayload = {
      event_type_id: "4850387a-8838-45d5-8c65-dce9d1f86de6",
      verifications: [
        {
          operation: "ADD",
          id: null,
          user_id: payload.user_id,
          is_verified: true,
        },
      ],
    };

    const response = await post("/modify-employee-validation", apiPayload, {
      instance: axiosSubscriptionInstance,
    });

    return { success: true, data: response };
  } catch (err) {
    const message =
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.message ||
      "Failed to verify employee";
    console.error("Verify error:", message, err);
    throw new Error(message);
  }
};

export const modifyDepartments = async (post, payload) => {
  try {
    const response = await post('/modify-departments', payload, { instance: axiosSubscriptionInstance });
    return { success: true, data: response?.data || response };
  } catch (err) {
    const message = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to modify departments';
    throw new Error(message);
  }
};

export const modifyRoles = async (post, payload) => {
  try {
    const response = await post('/modify-roles', payload, { instance: axiosSubscriptionInstance });
    return { success: true, data: response?.data || response };
  } catch (err) {
    const message = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to modify roles';
    throw new Error(message);
  }
};

// ✅ Assign or update manager for an employee role
export const modifyEmployeeRoleManagers = async (post, payload) => {
  try {
    const response = await post("/modify-employee-role-managers", payload, {
      instance: axiosSubscriptionInstance,
    });
    return { success: true, data: response?.data || response };
  } catch (err) {
    const message =
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.message ||
      "Failed to assign manager";
    console.error("Modify manager error:", message, err);
    throw new Error(message);
  }
};
